'use client';

import { useCallback } from 'react';
import { useAppSelector } from '@/hooks/useRedux';
import { selectIsLoggedIn } from '@/redux/auth/signin/slice';
import { useRouter, usePathname, useSearchParams } from 'next/navigation';
import { isLoggingOut } from '@/utils/session';

type UseAuthGuardOptions = {
  loginPath?: string;
  onRedirect?: () => void;
};

export function useAuthGuard({
  loginPath = '/account/signin',
  onRedirect,
}: UseAuthGuardOptions = {}) {
  const isAuthenticated = useAppSelector(selectIsLoggedIn);
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const getCurrentPath = useCallback(() => {
    if (!pathname) return '/';
    const qs = searchParams?.toString();
    return qs ? `${pathname}?${qs}` : pathname;
  }, [pathname, searchParams]);

  const redirectToLogin = useCallback(
    (targetPath?: string) => {
      if (isLoggingOut()) return;

      const path = targetPath || getCurrentPath();

      if (typeof window !== 'undefined') {
        const absPath = new URL(path, window.location.origin).toString();
        try {
          sessionStorage.setItem('postLoginRedirect', path);
          sessionStorage.setItem('postLoginRedirectAbs', absPath);
        } catch {
        }
      }

      onRedirect?.();

      const hasQuery = loginPath.includes('?');
      const sep = hasQuery ? '&' : '?';
      const loginUrl = `${loginPath}${sep}next=${encodeURIComponent(path)}`;

      router.push(loginUrl);
    },
    [getCurrentPath, loginPath, onRedirect, router]
  );

  const guard = useCallback(
    (requiresAuth: boolean | undefined, targetPath: string): boolean => {
      if (requiresAuth && !isAuthenticated) {
        redirectToLogin(targetPath);
        return true;
      }
      return false;
    },
    [isAuthenticated, redirectToLogin]
  );

  return { guard, isAuthenticated, redirectToLogin };
}
